import { Link } from "react-router-dom";

/**
 * LegalDoc — Renders a structured legal document.
 * Body blocks:
 *   { p: "paragraph text" }
 *   { h: "sub-heading (e.g. Step Two — Vendor Proposal)" }
 *   { list: ["item 1", "item 2"] }              // bulleted
 *   { defs: [["Term", "definition body"], ...]} // definition list
 *   { contact: { company, address, email, website } }
 */
export default function LegalDoc({ doc, related = [] }) {
  return (
    <div data-testid={`legal-doc-${doc.slug}`} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16">
      <div className="eyebrow mb-3">{doc.role_label}</div>
      <h1 className="text-4xl sm:text-5xl mb-3">{doc.title}</h1>
      <div className="text-sm text-[color:var(--text-muted)]">
        Version {doc.version} · Effective {doc.effective_date}
      </div>

      {/* Intro */}
      <div className="mt-8 space-y-4">
        {doc.intro.map((t, i) => (
          <p key={i} className="text-[color:var(--text-muted)] leading-relaxed">{t}</p>
        ))}
      </div>

      {/* Articles */}
      <div className="mt-10 space-y-4">
        {doc.articles.map((a) => (
          <section
            key={a.n}
            id={`article-${a.n}`}
            data-testid={`article-${a.n}`}
            className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8 scroll-mt-24"
          >
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[color:var(--accent)]">
                {doc.section_label || "Article"} {a.n}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold mb-4">{a.title}</h2>
            <div className="space-y-4">
              {a.body.map((block, i) => <Block key={i} block={block} />)}
            </div>
          </section>
        ))}
      </div>

      {/* Closing */}
      {doc.closing && (
        <p className="mt-10 text-sm text-[color:var(--text-muted)] leading-relaxed italic">
          {doc.closing}
        </p>
      )}

      {/* Related docs */}
      {related.length > 0 && (
        <div className="mt-12 pt-8 border-t border-[color:var(--border)]">
          <div className="eyebrow mb-3">Related documents</div>
          <ul className="space-y-2">
            {related.map((r) => (
              <li key={r.to}>
                <Link to={r.to} className="link-underline text-[color:var(--text)]">
                  {r.label} →
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Block({ block }) {
  if (block.p) {
    return <p className="text-[color:var(--text-muted)] leading-relaxed">{block.p}</p>;
  }
  if (block.h) {
    return <h3 className="text-base font-semibold text-[color:var(--text)] mt-4">{block.h}</h3>;
  }
  if (block.list) {
    return (
      <ul className="space-y-2 pl-1">
        {block.list.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[color:var(--text-muted)] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.defs) {
    return (
      <dl className="space-y-3">
        {block.defs.map(([term, def], i) => (
          <div key={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
            <dt className="font-semibold text-[color:var(--text)] sm:min-w-[130px] shrink-0">{term}</dt>
            <dd className="text-[color:var(--text-muted)] leading-relaxed">means {def}</dd>
          </div>
        ))}
      </dl>
    );
  }
  if (block.contact) {
    const c = block.contact;
    return (
      <div className="text-sm text-[color:var(--text)] leading-relaxed" data-testid="legal-contact">
        <div className="font-semibold">{c.company}</div>
        {c.address && <div className="text-[color:var(--text-muted)]">{c.address}</div>}
        {c.email && <div className="text-[color:var(--text-muted)]">{c.email}</div>}
        {c.phone && <div className="text-[color:var(--text-muted)]">{c.phone}</div>}
        {c.website && <div className="text-[color:var(--text-muted)]">{c.website}</div>}
      </div>
    );
  }
  return null;
}
